import type { BrowserTrustedDeviceFailureKind } from "../../../core/account/services/browser-trusted-device-error.ts";
import type { InAppBrowser } from "./browser-environment.ts";

/**
 * One way of proving a browser installation, strongest first.
 *
 * - `webCrypto` — non-extractable ECDSA key in IndexedDB (default).
 * - `webCryptoExtractable` — ECDSA key as a JWK in localStorage.
 * - `deviceCookie` — server-issued secret in an HttpOnly cookie.
 *
 * @example
 * const rung: TrustedDeviceRung = "webCrypto";
 */
export type TrustedDeviceRung =
  | "webCrypto"
  | "webCryptoExtractable"
  | "deviceCookie";

/**
 * Specific advice shown when no rung can work in this browser.
 *
 * @example
 * const reason: BrowserHelpReason = "inAppBrowser";
 */
export type BrowserHelpReason =
  | "insecureConnection"
  | "inAppBrowser"
  | "privateMode"
  | "unsupported";

/**
 * Synchronous facts about the browser the ladder decides from.
 *
 * @example
 * const environment = getTrustedDeviceEnvironment(window);
 */
export type TrustedDeviceEnvironment = {
  /** `crypto.subtle` exists (secure context + WebCrypto). */
  webCrypto: boolean;
  /** `indexedDB.open` exists. */
  indexedDb: boolean;
  /** localStorage can be written and read back. */
  localStorage: boolean;
  /** `navigator.cookieEnabled` is true. */
  cookiesEnabled: boolean;
  /** The page is on plain HTTP on a public host (no Secure cookies/WebCrypto). */
  insecurePublicPage: boolean;
  inAppBrowser: InAppBrowser;
  /** This browser already enrolled an extractable JWK installation. */
  hasExtractableInstallation: boolean;
  /** This browser previously fell back to the device cookie. */
  prefersDeviceCookie: boolean;
};

const STRUCTURAL_STORAGE_FAILURES: ReadonlySet<BrowserTrustedDeviceFailureKind> =
  new Set(["storageUnavailable", "keyNotStorable", "storageQuota"]);

const KEY_FAILURES: ReadonlySet<BrowserTrustedDeviceFailureKind> = new Set([
  "keyUnsupported",
  "signingFailed",
]);

function canUseDeviceCookie(environment: TrustedDeviceEnvironment): boolean {
  return environment.cookiesEnabled && !environment.insecurePublicPage;
}

function cookieRungIfAvailable(
  environment: TrustedDeviceEnvironment,
): TrustedDeviceRung | undefined {
  return canUseDeviceCookie(environment) ? "deviceCookie" : undefined;
}

/**
 * Pick the first rung to try for this browser.
 *
 * @remarks
 * The strong `webCrypto` rung is always first whenever WebCrypto and
 * IndexedDB exist, so a capable browser is never downgraded. An HTTP page
 * gets no rung at all: neither WebCrypto nor `Secure` cookies work there.
 *
 * @example
 * initialTrustedDeviceRung(environment); // "webCrypto"
 */
export function initialTrustedDeviceRung(
  environment: TrustedDeviceEnvironment,
): TrustedDeviceRung | undefined {
  if (environment.insecurePublicPage) return undefined;

  if (environment.webCrypto && environment.indexedDb) return "webCrypto";

  if (
    environment.webCrypto &&
    environment.localStorage &&
    (environment.hasExtractableInstallation ||
      !environment.prefersDeviceCookie)
  ) {
    return "webCryptoExtractable";
  }

  return cookieRungIfAvailable(environment);
}

/**
 * Whether the strong rung may mint a new key in this browser.
 *
 * A browser that already enrolled through a fallback keeps using it, so a
 * new strong key never costs the student an extra device slot.
 *
 * @example
 * createBrowserTrustedDeviceProof(request, { allowCreate: mayCreateStrongKey(environment) });
 */
export function mayCreateStrongKey(
  environment: TrustedDeviceEnvironment,
): boolean {
  return (
    !environment.hasExtractableInstallation && !environment.prefersDeviceCookie
  );
}

/**
 * Pick the next rung after a local failure, or nothing when the failure is
 * transient (another tab, timeout, network) and must be reported instead.
 *
 * @example
 * nextTrustedDeviceRung("webCrypto", "keyNotStorable", environment); // "webCryptoExtractable"
 */
export function nextTrustedDeviceRung(
  current: TrustedDeviceRung,
  failure: BrowserTrustedDeviceFailureKind | undefined,
  environment: TrustedDeviceEnvironment,
): TrustedDeviceRung | undefined {
  if (!failure) return undefined;

  if (current === "webCrypto") {
    if (failure === "notEnrolled") {
      return environment.hasExtractableInstallation && environment.webCrypto
        ? "webCryptoExtractable"
        : cookieRungIfAvailable(environment);
    }

    if (STRUCTURAL_STORAGE_FAILURES.has(failure)) {
      return environment.webCrypto &&
        environment.localStorage &&
        !environment.prefersDeviceCookie
        ? "webCryptoExtractable"
        : cookieRungIfAvailable(environment);
    }

    if (KEY_FAILURES.has(failure)) return cookieRungIfAvailable(environment);

    return undefined;
  }

  if (current === "webCryptoExtractable") {
    if (
      failure === "notEnrolled" ||
      STRUCTURAL_STORAGE_FAILURES.has(failure) ||
      KEY_FAILURES.has(failure)
    ) {
      return cookieRungIfAvailable(environment);
    }

    return undefined;
  }

  return undefined;
}

/**
 * Choose the advice for a browser where every rung failed.
 *
 * @example
 * resolveBrowserHelpReason(environment, true); // "inAppBrowser" | "privateMode" | ...
 */
export function resolveBrowserHelpReason(
  environment: Pick<TrustedDeviceEnvironment, "insecurePublicPage" | "inAppBrowser">,
  privateModeSuspected: boolean,
): BrowserHelpReason {
  if (environment.insecurePublicPage) return "insecureConnection";

  if (environment.inAppBrowser !== "none") return "inAppBrowser";

  if (privateModeSuspected) return "privateMode";

  return "unsupported";
}
